import { useGetRegions } from '@/api/hooks/useRegion';
import {
  SociumIntegration,
  useCreateSociumIntegration,
  useListSociumIntegrations,
  useRotateSociumIntegrationKey,
  useSetSociumIntegrationStatus,
  useSociumDiagnostics,
  useUpdateSociumIntegration,
} from '@/api/hooks/useSocium';
import { Header } from '@/components/Layout/Header/Header';
import { Modal } from '@/components/Layout';
import { useToast } from '@/hooks/useToast';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const ENV_OPTIONS = [
  { label: 'Staging', value: 'staging' },
  { label: 'Production', value: 'production' },
];

const emptyForm = {
  name: '',
  environment: 'staging',
  schoolMode: 'new' as 'new' | 'existing',
  schoolName: '',
  schoolId: '',
  regionId: '',
  allowedRedirectDomains: '',
};

const parseDomains = (raw: string) =>
  raw
    .split(/[\n,]/)
    .map((domain) => domain.trim())
    .filter(Boolean);

const inputClass =
  'bg-[#2A2A2A] border border-[#8080803d] rounded px-3 py-2 text-white text-sm w-full outline-none focus:border-primary';
const labelClass = 'flex flex-col gap-1 text-sm font-semibold text-[#939393]';

const Pill = ({
  text,
  tone,
}: {
  text: string;
  tone: 'ok' | 'warn' | 'danger' | 'info';
}) => {
  const tones = {
    ok: 'bg-[#1E3A24] text-[#4ADE80]',
    warn: 'bg-[#3A2E12] text-[#FEC200]',
    danger: 'bg-[#3A1A1A] text-[#F87171]',
    info: 'bg-[#1A2A3A] text-[#60A5FA]',
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-semibold ${tones[tone]}`}>
      {text}
    </span>
  );
};

export const Socium = () => {
  const { showToast } = useToast();
  const { data: integrations, isLoading } = useListSociumIntegrations();
  const { data: regions } = useGetRegions();
  const createMutation = useCreateSociumIntegration();
  const updateMutation = useUpdateSociumIntegration();
  const rotateMutation = useRotateSociumIntegrationKey();
  const statusMutation = useSetSociumIntegrationStatus();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [editing, setEditing] = useState<SociumIntegration | null>(null);
  const [editDomains, setEditDomains] = useState('');
  const navigate = useNavigate();
  const [diagFor, setDiagFor] = useState<SociumIntegration | null>(null);
  const [diagType, setDiagType] = useState('user');
  const [diagRef, setDiagRef] = useState('');
  const diagnose = useSociumDiagnostics();

  const runDiagnose = () => {
    if (!diagFor || !diagRef.trim()) return;
    diagnose.mutate({
      integrationId: diagFor._id,
      type: diagType,
      ref: diagRef.trim(),
    });
  };

  const lineColor: Record<string, string> = {
    ok: 'text-[#4ADE80]',
    warn: 'text-[#FEC200]',
    error: 'text-[#F87171]',
    info: 'text-[#939393]',
  };

  const regionOptions = useMemo(() => regions ?? [], [regions]);

  const resetCreate = () => {
    setForm(emptyForm);
    setIsCreateOpen(false);
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      showToast('Le nom du client est requis.', 'error');
      return;
    }
    if (
      form.schoolMode === 'new' &&
      (!form.schoolName.trim() || !form.regionId)
    ) {
      showToast('Nouveau client : nom et région requis.', 'error');
      return;
    }
    if (form.schoolMode === 'existing' && !form.schoolId.trim()) {
      showToast('École existante : identifiant requis.', 'error');
      return;
    }

    try {
      const result = await createMutation.mutateAsync({
        name: form.name.trim(),
        environment: form.environment,
        ...(form.schoolMode === 'existing'
          ? { schoolId: form.schoolId.trim() }
          : { schoolName: form.schoolName.trim(), regionId: form.regionId }),
        allowedRedirectDomains: parseDomains(form.allowedRedirectDomains),
      });
      resetCreate();
      setRevealedKey(result.apiKey);
      showToast('Intégration créée.', 'success');
    } catch (error) {
      showToast((error as Error).message, 'error');
    }
  };

  const handleRotate = async (integration: SociumIntegration) => {
    try {
      const result = await rotateMutation.mutateAsync(integration._id);
      setRevealedKey(result.apiKey);
      showToast('Nouvelle clé générée — l’ancienne est révoquée.', 'success');
    } catch (error) {
      showToast((error as Error).message, 'error');
    }
  };

  const handleToggleStatus = async (integration: SociumIntegration) => {
    const next = integration.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      await statusMutation.mutateAsync({
        integrationId: integration._id,
        status: next,
      });
      showToast(
        next === 'ACTIVE' ? 'Intégration activée.' : 'Intégration désactivée.',
        'success',
      );
    } catch (error) {
      showToast((error as Error).message, 'error');
    }
  };

  const handleSaveDomains = async () => {
    if (!editing) return;
    try {
      await updateMutation.mutateAsync({
        integrationId: editing._id,
        allowedRedirectDomains: parseDomains(editDomains),
      });
      setEditing(null);
      showToast('Domaines de redirection mis à jour.', 'success');
    } catch (error) {
      showToast((error as Error).message, 'error');
    }
  };

  const headerCol = 'text-primary bg-[#131311] text-sm font-bold';
  const bodyCol = 'text-white bg-[#131311] border-b border-[#8080803d] text-sm';

  return (
    <>
      <Header title="Intégrations Socium" goBack />

      <div className="px-6 py-4">
        <div className="flex justify-between items-center bg-[#232323] py-4 px-7 rounded-[8px]">
          <p className="text-white text-lg font-bold">
            Clients & clés d’intégration
          </p>
          <button
            onClick={() => setIsCreateOpen(true)}
            className="bg-primary text-black font-bold text-sm px-5 h-[41px] rounded flex items-center gap-2"
          >
            <i className="pi pi-plus" /> Nouveau client
          </button>
        </div>

        <div className="bg-[#131311] p-4 mt-4 rounded-[8px]">
          <DataTable
            value={integrations ?? []}
            loading={isLoading}
            emptyMessage="Aucune intégration."
          >
            <Column
              field="name"
              header="Client"
              headerClassName={headerCol}
              bodyClassName={bodyCol}
            />
            <Column
              header="École"
              headerClassName={headerCol}
              bodyClassName={bodyCol}
              body={(row: SociumIntegration) => row.school?.name ?? '—'}
            />
            <Column
              header="Env."
              headerClassName={headerCol}
              bodyClassName={bodyCol}
              body={(row: SociumIntegration) => (
                <Pill
                  text={row.environment}
                  tone={row.environment === 'production' ? 'danger' : 'info'}
                />
              )}
            />
            <Column
              header="Statut"
              headerClassName={headerCol}
              bodyClassName={bodyCol}
              body={(row: SociumIntegration) => (
                <Pill
                  text={row.status}
                  tone={row.status === 'ACTIVE' ? 'ok' : 'warn'}
                />
              )}
            />
            <Column
              header="Clé"
              headerClassName={headerCol}
              bodyClassName={bodyCol}
              body={(row: SociumIntegration) => (
                <span className="font-mono text-xs text-[#939393]">
                  {row.apiKeyPrefix ? `${row.apiKeyPrefix}…` : '—'}
                </span>
              )}
            />
            <Column
              header="Domaines de retour"
              headerClassName={headerCol}
              bodyClassName={bodyCol}
              body={(row: SociumIntegration) =>
                row.allowedRedirectDomains?.length
                  ? row.allowedRedirectDomains.join(', ')
                  : '—'
              }
            />
            <Column
              header="Actions"
              headerClassName={headerCol}
              bodyClassName={bodyCol}
              body={(row: SociumIntegration) => (
                <div className="flex gap-3">
                  <button
                    title="Voir le détail"
                    className="text-[#4ADE80] hover:text-white"
                    onClick={() => navigate(`/socium/${row._id}`)}
                  >
                    <i className="pi pi-eye" />
                  </button>
                  <button
                    title="Diagnostic support"
                    className="text-[#C084FC] hover:text-white"
                    onClick={() => {
                      setDiagFor(row);
                      setDiagRef('');
                      diagnose.reset();
                    }}
                  >
                    <i className="pi pi-search" />
                  </button>
                  <button
                    title="Domaines de retour"
                    className="text-[#60A5FA] hover:text-white"
                    onClick={() => {
                      setEditing(row);
                      setEditDomains(
                        (row.allowedRedirectDomains ?? []).join('\n'),
                      );
                    }}
                  >
                    <i className="pi pi-link" />
                  </button>
                  <button
                    title="Régénérer la clé"
                    className="text-primary hover:text-white"
                    onClick={() => handleRotate(row)}
                  >
                    <i className="pi pi-refresh" />
                  </button>
                  <button
                    title={
                      row.status === 'ACTIVE' ? 'Désactiver' : 'Activer'
                    }
                    className={
                      row.status === 'ACTIVE'
                        ? 'text-[#F87171] hover:text-white'
                        : 'text-[#4ADE80] hover:text-white'
                    }
                    onClick={() => handleToggleStatus(row)}
                  >
                    <i
                      className={
                        row.status === 'ACTIVE' ? 'pi pi-ban' : 'pi pi-check'
                      }
                    />
                  </button>
                </div>
              )}
            />
          </DataTable>
        </div>
      </div>

      {/* Création */}
      <Modal
        isOpen={isCreateOpen}
        onClose={resetCreate}
        headerStyle="bg-carbon h-[36px]"
        modalStyle="bg-carbon"
      >
        <div className="flex flex-col gap-4 w-[460px] max-w-full p-2">
          <h3 className="text-white text-lg font-bold">Nouveau client Socium</h3>

          <label className={labelClass}>
            Nom du client
            <input
              className={inputClass}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="SKOP"
            />
          </label>

          <label className={labelClass}>
            Environnement
            <select
              className={inputClass}
              value={form.environment}
              onChange={(e) =>
                setForm({ ...form, environment: e.target.value })
              }
            >
              {ENV_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <div className="flex gap-2 text-sm">
            {(['new', 'existing'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setForm({ ...form, schoolMode: mode })}
                className={`flex-1 py-2 rounded border ${
                  form.schoolMode === mode
                    ? 'border-primary text-primary font-semibold'
                    : 'border-[#8080803d] text-[#939393]'
                }`}
              >
                {mode === 'new' ? 'Nouvelle école' : 'École existante'}
              </button>
            ))}
          </div>

          {form.schoolMode === 'new' ? (
            <>
              <label className={labelClass}>
                Nom de l’école
                <input
                  className={inputClass}
                  value={form.schoolName}
                  onChange={(e) =>
                    setForm({ ...form, schoolName: e.target.value })
                  }
                />
              </label>
              <label className={labelClass}>
                Région
                <select
                  className={inputClass}
                  value={form.regionId}
                  onChange={(e) =>
                    setForm({ ...form, regionId: e.target.value })
                  }
                >
                  <option value="">Sélectionner</option>
                  {regionOptions.map((region) => (
                    <option key={region._id} value={region._id}>
                      {region.name}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : (
            <label className={labelClass}>
              Identifiant de l’école
              <input
                className={inputClass}
                value={form.schoolId}
                onChange={(e) =>
                  setForm({ ...form, schoolId: e.target.value })
                }
                placeholder="6650…"
              />
            </label>
          )}

          <label className={labelClass}>
            Domaines de retour (séparés par une virgule)
            <input
              className={inputClass}
              value={form.allowedRedirectDomains}
              onChange={(e) =>
                setForm({ ...form, allowedRedirectDomains: e.target.value })
              }
              placeholder="app.socium.sn"
            />
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button className="text-[#939393] text-sm" onClick={resetCreate}>
              Annuler
            </button>
            <button
              className="bg-primary text-black font-bold text-sm px-5 py-2 rounded disabled:opacity-50"
              disabled={createMutation.isPending}
              onClick={handleCreate}
            >
              {createMutation.isPending ? 'Création…' : 'Créer'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Affichage unique de la clé */}
      <Modal
        isOpen={!!revealedKey}
        onClose={() => setRevealedKey(null)}
        headerStyle="bg-carbon h-[36px]"
        modalStyle="bg-carbon"
      >
        <div className="flex flex-col gap-3 w-[500px] max-w-full p-2">
          <h3 className="text-white text-lg font-bold">
            Clé API — affichée une seule fois
          </h3>
          <p className="text-sm text-[#939393]">
            Copiez cette clé maintenant et transmettez-la à Socium par canal
            sécurisé. Elle ne sera plus jamais affichée — en cas de perte,
            régénérez-la (l’ancienne sera révoquée).
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all bg-[#131311] border border-[#8080803d] rounded px-3 py-2 text-sm text-primary">
              {revealedKey}
            </code>
            <button
              className="bg-[#2A2A2A] text-white px-3 py-2 rounded"
              onClick={() => {
                if (revealedKey) navigator.clipboard.writeText(revealedKey);
                showToast('Clé copiée.', 'success');
              }}
            >
              <i className="pi pi-copy" />
            </button>
          </div>
          <div className="flex justify-end pt-2">
            <button
              className="bg-primary text-black font-bold text-sm px-5 py-2 rounded"
              onClick={() => setRevealedKey(null)}
            >
              J’ai copié la clé
            </button>
          </div>
        </div>
      </Modal>

      {/* Diagnostic support */}
      <Modal
        isOpen={!!diagFor}
        onClose={() => setDiagFor(null)}
        headerStyle="bg-carbon h-[36px]"
        modalStyle="bg-carbon"
      >
        <div className="flex flex-col gap-4 w-[560px] max-w-full p-2">
          <h3 className="text-white text-lg font-bold">
            Diagnostic — {diagFor?.name}
          </h3>
          <p className="text-sm text-[#939393]">
            Collez la référence Socium remontée par le ticket pour voir son état
            chez Yello (de quel côté est le souci).
          </p>
          <div className="flex gap-2">
            <select
              className={`${inputClass} w-40`}
              value={diagType}
              onChange={(e) => setDiagType(e.target.value)}
            >
              <option value="user">Utilisateur (ref)</option>
              <option value="formation">Formation (ref)</option>
              <option value="session">Session (ref)</option>
              <option value="email">Email</option>
            </select>
            <input
              className={inputClass}
              value={diagRef}
              onChange={(e) => setDiagRef(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runDiagnose()}
              placeholder={
                diagType === 'email' ? 'binta@client.com' : 'socium-user-77'
              }
            />
            <button
              className="bg-primary text-black font-bold text-sm px-5 rounded disabled:opacity-50"
              disabled={diagnose.isPending || !diagRef.trim()}
              onClick={runDiagnose}
            >
              {diagnose.isPending ? '…' : 'Chercher'}
            </button>
          </div>

          {diagnose.isError && (
            <p className="text-[#F87171] text-sm">
              {diagnose.error.message}
            </p>
          )}

          {diagnose.data && (
            <div className="bg-[#131311] border border-[#8080803d] rounded-[8px] p-4 flex flex-col gap-2">
              {diagnose.data.lines.map((line, index) => (
                <div key={index} className="flex gap-3 text-sm">
                  <span className="text-[#939393] w-40 flex-shrink-0">
                    {line.label}
                  </span>
                  <span className={lineColor[line.level] ?? 'text-white'}>
                    {line.value}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-1">
            <button
              className="bg-[#2A2A2A] text-white text-sm px-5 py-2 rounded"
              onClick={() => setDiagFor(null)}
            >
              Fermer
            </button>
          </div>
        </div>
      </Modal>

      {/* Domaines de retour */}
      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        headerStyle="bg-carbon h-[36px]"
        modalStyle="bg-carbon"
      >
        <div className="flex flex-col gap-3 w-[440px] max-w-full p-2">
          <h3 className="text-white text-lg font-bold">
            Domaines de retour — {editing?.name}
          </h3>
          <p className="text-sm text-[#939393]">
            Un domaine par ligne. La liste remplace la précédente (anti-phishing
            des URLs de retour vers Socium).
          </p>
          <textarea
            value={editDomains}
            onChange={(e) => setEditDomains(e.target.value)}
            rows={4}
            className={`${inputClass} font-mono`}
            placeholder={'app.socium.sn\nstaging.socium.sn'}
          />
          <div className="flex justify-end gap-3 pt-2">
            <button
              className="text-[#939393] text-sm"
              onClick={() => setEditing(null)}
            >
              Annuler
            </button>
            <button
              className="bg-primary text-black font-bold text-sm px-5 py-2 rounded disabled:opacity-50"
              disabled={updateMutation.isPending}
              onClick={handleSaveDomains}
            >
              Enregistrer
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
